import React, { Fragment, type ReactElement } from 'react'

import { useCallback, useMemorizedState, MemorizedStateStorages } from 'hooks'

import type {
  PortfolioPointOfInterestList,
  PortfolioSecurityPointOfInterestList,
} from 'app/effector/pointsOfInterest/models'

import { LabelledToggle } from 'components/atoms/LabelledToggle'
import { Paper } from 'components/atoms/Paper'

import type {
  IncomeLineDataItem,
  PortfolioPointOfInterestCardProps,
  SecuritySlicePointOfInterestCardProps,
} from 'components/organisms/charts/parts'
import { PriceChart, type PriceChartDashedLines } from 'components/organisms/PriceChart'

type SecurityChartProps = {
  data: IncomeLineDataItem[]
  period: string
  periodPriceData: { name: string; percent: number; price: number }
  topContent?: ReactElement
  topContentOffset?: number
  portfolioId?: number
  securityId?: number
  dashedLines?: PriceChartDashedLines[]
  pointsOfInterest?: PortfolioPointOfInterestList | PortfolioSecurityPointOfInterestList
  PointOfInterestCard?: (
    props: SecuritySlicePointOfInterestCardProps | PortfolioPointOfInterestCardProps,
  ) => React.ReactElement | null
}

const SecurityChart = ({
  data,
  period,
  periodPriceData,
  topContent,
  topContentOffset,
  portfolioId,
  securityId,
  dashedLines,
  pointsOfInterest,
  PointOfInterestCard,
}: SecurityChartProps): React.ReactElement => {
  const [pointsOfInterestVisible, setPointsOfInterestVisible] = useMemorizedState(
    `Portfolio/${portfolioId}/Security/${securityId}/PointsOfInterestVisible`,
    false,
    {
      storage: MemorizedStateStorages.LOCAL,
    },
  )

  const handleChangePointsOfInterestVisible = useCallback(
    (event) => {
      const visible = event.target.checked
      setPointsOfInterestVisible(visible)
    },
    [setPointsOfInterestVisible],
  )

  return (
    <Fragment>
      <PriceChart
        period={period}
        data={data}
        negative={(periodPriceData.price ?? 0) < 0}
        topContent={topContent}
        topContentOffset={topContentOffset}
        portfolioId={portfolioId}
        securityId={securityId}
        dashedLines={dashedLines}
        pointsOfInterest={pointsOfInterestVisible ? pointsOfInterest : []}
        PointOfInterestCard={PointOfInterestCard}
      />
      {(pointsOfInterest ?? []).length > 0 && (
        <Paper top={24}>
          <LabelledToggle
            label="Show buy/sell activity"
            checked={pointsOfInterestVisible}
            onChange={handleChangePointsOfInterestVisible}
          />
        </Paper>
      )}
    </Fragment>
  )
}

export { SecurityChart }
